import { useState } from "react";
import { Link } from "react-router-dom";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { PiedDeListe } from "@/composants/ui/pied-de-liste";
import { LegendeEtapes, Timeline } from "@/composants/timeline";
import { useListePaginee } from "@/lib/chargement";
import { statutLot } from "@/lib/libelles";
import { api, type Lot } from "@/lib/api";

/**
 * Les lots du périmètre, avec leur timeline et leur prochaine action.
 *
 * C'est l'écran que le LOT 0 avait posé pour rendre ses coutures exerçables. Il
 * reste, parce que le terrain raisonne en lots là où le bureau raisonne en
 * dossiers — et parce que la prochaine action d'un poseur est ici, pas ailleurs.
 */
export function PageLots() {
  const [terme, setTerme] = useState("");
  const [cherche, setCherche] = useState("");
  const liste = useListePaginee<Lot>(
    (decalage, signal) => api.lots({ q: cherche || undefined, decalage }, signal),
    [cherche],
  );

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Lots</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Chaque lot porte ses propres jalons, indépendamment des autres de la même Commande / Projet.
        </p>
      </div>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          setCherche(terme.trim());
        }}
      >
        <Champ
          libelle="Filtrer"
          className="min-w-[14rem] flex-1"
          placeholder="Code, intitulé, adresse, ville…"
          value={terme}
          onChange={(evenement) => setTerme(evenement.target.value)}
        />
      </form>

      {liste.erreur ? <Erreur message={liste.erreur} onReessayer={liste.recharger} /> : null}
      {!liste.erreur && liste.enCours ? <Chargement message="Chargement des lots…" /> : null}

      {!liste.enCours && liste.total === 0 ? (
        <Carte>
          <Vide
            titre="Aucun lot"
            detail={
              cherche
                ? "Aucun lot de votre périmètre ne correspond à cette recherche."
                : "Les lots apparaissent ici dès qu'une Commande / Projet est découpée."
            }
          />
        </Carte>
      ) : null}

      {liste.elements.length > 0 ? <LegendeEtapes /> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {liste.elements.map((lot) => (
          <Carte key={lot.id}>
            <EnTeteCarte className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Link
                  to={`/lots/${lot.id}`}
                  className="font-mono text-sm text-encre-douce underline underline-offset-4"
                >
                  {lot.code_lot}
                </Link>
                <p className="font-medium text-encre">{lot.intitule}</p>
                <p className="text-sm text-encre-douce">
                  {lot.categorie_libelle} — {lot.adresse_realisation?.ville ?? "emport"}
                </p>
              </div>
              <Etiquette>{statutLot(lot.statut_affiche)}</Etiquette>
            </EnTeteCarte>
            <CorpsCarte>
              <Timeline etapes={lot.etapes} prochaineAction={lot.prochaine_action} />
            </CorpsCarte>
          </Carte>
        ))}
      </div>

      {liste.total > 0 ? (
        <Carte>
          <PiedDeListe
            nom="lot"
            affiches={liste.elements.length}
            total={liste.total}
            reste={liste.reste}
            enCours={liste.chargeLaSuite}
            onCharger={liste.chargerLaSuite}
          />
        </Carte>
      ) : null}
    </div>
  );
}
